<template>
    <div class="pay-in-details-modal__overlay" @click.self="$emit('close')">

        <div class="pay-in-details-modal__container bg-lightest d-flex flex-column">

            <div v-if="isConfirmingDelete" class="pay-in-details-modal__confirm-overlay">
                <div class="pay-in-details-modal__confirm-box bg-lightest">
                    <div class="fs-large mt-4 mb-3 text-center">Delete Payment</div>
                    <div class="text-center px-4 pb-4">Are you sure you want to delete this payment?</div>
                    <div class="w-100 d-flex justify-content-center pb-4">
                        <button class="btn tertiary px-5 mr-2" :disabled="isDeleting"
                            @click="isConfirmingDelete = false">No</button>
                        <loading-button class="btn success px-5" :loading="isDeleting" @click="confirmDelete">Yes</loading-button>
                    </div>
                </div>
            </div>

            <create-invoice-modal v-if="showCreateInvoiceModal" :reservation="reservation"
                :paymentId="invoicePaymentId !== null ? invoicePaymentId : (payment.isGrouped ? undefined : payment.id)"
                :transactionTotal="invoicePaymentId !== null ? payment.value : reservation.billingTotal"
                :transactionCode="invoicePaymentId !== null ? undefined : (payment.isGrouped ? payment.transactionCode : undefined)"
                @created="onInvoiceCreated" @close="toggleCreateInvoiceModal"></create-invoice-modal>

            <div class="pay-in-details-modal__header px-4 pt-3">
                <div class="text-center fs-larger font-weight-600 pb-2 dark-top">
                    Pay In Details
                </div>
            </div>

            <div class="pay-in-details-modal__body px-4">
                <hr class="pay-in-details-modal__divider">

                <!-- Date, Payment Method, Amount -->
                <div class="form-group row pay-in-details-modal__padded-row">
                    <div class="col-sm-4">
                        <label class="full">
                            Date
                            <input type="text" :value="formattedDate" disabled>
                        </label>
                    </div>
                    <div class="col-sm-4">
                        <label class="full">
                            Payment Method
                            <input type="text" :value="paymentMethodLabel" disabled>
                        </label>
                    </div>
                    <div class="col-sm-4">
                        <label class="full">
                            Amount
                            <input type="text" :value="formattedAmount" disabled>
                        </label>
                    </div>
                </div>

                <!-- Payment summary cards -->
                <div class="pay-in-details-modal__padded-row">
                    <pay-in-summary-card v-for="(p, i) in displayPayments" :key="p.id"
                        :payment="buildSummaryForPayment(p)" :index="i" :isPartial="!p.isFullAmount" :collapsible="true"
                        :amount="p.paidValue" :currency="p.currency" :showBillingRow="!p.hasBillingDetails"
                        class="mb-2">
                        <!-- Billing details -->
                        <billing-details-form v-if="p.hasBillingDetails" :billingDetails="p.billing.details"
                            :readOnly="true" />

                        <!-- Invoices (when the payment has invoices, or one is being created) -->
                        <template v-if="(p.invoices && p.invoices.length > 0) || invoiceCreating">
                            <div class="font-weight-600 mt-3 mb-2">Invoices</div>
                            <div class="pay-in-details-modal__invoice-table position-relative">
                                <div v-if="invoiceCreating" class="pay-in-details-modal__invoice-loader">
                                    <loader></loader>
                                </div>
                                <table class="w-100">
                                    <thead>
                                        <tr>
                                            <th class="bold">Date</th>
                                            <th class="bold">Number</th>
                                            <th class="bold">Type</th>
                                            <th class="bold">Client</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(invoice, invoiceIndex) in p.invoices" :key="invoiceIndex"
                                            @click="openInvoiceDetailsModal(invoice)"
                                            class="pay-in-details-modal__invoice-row">
                                            <td>{{ invoice.date }}</td>
                                            <td>{{ invoice.invoicE_NumeroDocumento }}/{{ invoice.invoicE_Serie }}</td>
                                            <td>
                                                <div class="d-flex align-items-center">
                                                    {{ invoiceTypeLabel(invoice) }}
                                                    <span v-if="!invoice.isActive"
                                                        class="lightest fs-smallest ml-2 px-3 py-1 bg-color-6 radius-stadium text-center font-weight-600">Canceled</span>
                                                </div>
                                            </td>
                                            <td>{{ invoice.invoiceName }}</td>
                                            <td @click.stop="openNonLegacyInvoicesModal(invoice)">
                                                <button v-if="legacyPayments.length > 0"
                                                    class="btn blue pay-in-details-modal__invoice-btn"
                                                    :disabled="!p.hasLegacyInvoices || nonLegacyPayments.length === 0"
                                                    :class="{ 'pay-in-details-modal__invoice-btn--disabled': !p.hasLegacyInvoices || nonLegacyPayments.length === 0 }"
                                                    :title="!p.hasLegacyInvoices || nonLegacyPayments.length === 0 ? 'No valid payments available' : ''">Transfer</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>

                        <!-- Actions -->
                        <div class="d-flex justify-content-end my-3">
                            <button v-if="canCreateInvoiceForPayment(p)" class="btn blue mr-2"
                                :class="{ 'pay-in-details-modal__btn--disabled': isReservationPending || invoiceCreating }"
                                :disabled="isReservationPending" @click="openCreateInvoiceForPayment(p.id)">Create
                                Invoice</button>
                            <button class="btn blue" :class="{ 'pay-in-details-modal__btn--disabled': invoiceCreating }"
                                :disabled="!p.canBeEdited || invoiceCreating" @click="$emit('edit', p)">Edit</button>
                        </div>
                    </pay-in-summary-card>
                </div>

            </div>

            <div class="pay-in-details-modal__footer px-4 py-3 d-flex justify-content-between align-items-center">
                <button v-if="payment.isLegacy" class="btn action" @click="showDeleteModal()">
                    Delete
                </button>

                <button class="btn tertiary" @click="$emit('close')">
                    Close
                </button>
            </div>
        </div>

        <non-legacy-invoices-modal v-if="isNonLegacyInvoiceModalOpen" :payments="nonLegacyPayments"
            :isSaving="isSaving" @save="onSavePayInEdit" @close="isNonLegacyInvoiceModalOpen = false" />
        <invoice-modal v-if="showInvoiceDetailsModal" :reservation="reservation"
            @toggleInvoiceModal="handleInvoiceModalToggle"></invoice-modal>
    </div>

</template>

<script>
import { reservationsMixin } from '@/mixins/reservations'
import BillingDetailsForm from '@/tools/modal/ReservationPayIn/BillingDetailsForm'
import PayInSummaryCard from '@/tools/modal/ReservationPayIn/PayInSummaryCard'
import NonLegacyInvoicesModal from './NonLegacyInvoicesModal'
import CreateInvoiceModal from '@/tools/Invoice/CreateInvoice'
import InvoiceModal from '@/tools/Invoice/InvoiceModal'
import Loader from '@/tools/loaders/loader1'
import LoadingButton from '@/tools/buttons/LoadingButton'
import { mapActions, mapGetters } from 'vuex'
import invoiceService from '@/api/reservation/invoiceService'
import { INVOICE_TYPES } from '@/enums/invoiceType'
import IntegrationType from '@/enums/integrationType'
import { RESERVATION_STATUS } from '@/enums/reservationStatus'

export default {
    mixins: [reservationsMixin],
    data() {
        return {
            isNonLegacyInvoiceModalOpen: false,
            isConfirmingDelete: false,
            isDeleting: false,
            isSaving: false,
            selectedInvoiceId: null,
            bookingId: null,
            showCreateInvoiceModal: false,
            showInvoiceDetailsModal: false,
            invoicePaymentId: null,
            invoiceCreating: false,
        }
    },
    watch: {
        payments() {
            this.invoiceCreating = false
        },
    },
    props: {
        payment: {
            type: Object,
            required: true
        },
        payments: {
            type: Array,
            default: () => []
        },
        reservation: {
            type: Object,
            required: true
        },
    },
    mounted() {
        document.body.classList.add('payin-details-open')
    },
    beforeDestroy() {
        document.body.classList.remove('payin-details-open')
    },
    computed: {
        ...mapGetters([
            'getIsSavingReservation',
            'getShowInvoiceOption',
            'getUserIntegrations',
            'getSelectedReservation'
        ]),
        isReservationPending() {
            return this.reservation && this.reservation.status === RESERVATION_STATUS.PENDING
        },
        isToShowInvoicing() {
            return this.getUserIntegrations && this.getUserIntegrations.some(
                integration => integration.type === IntegrationType.Invoicing
            );
        },
        nonLegacyPayments() {
            return this.payments.filter(p => !p.isLegacy)
        },
        legacyPayments() {
            return this.payments.filter(p => p.isLegacy)
        },
        formattedDate() {
            if (!this.payment.createdOn) return '-'
            return this.payment.createdOn;
        },
        paymentMethodLabel() {
            if (this.payment.allocations.length === 0) {
                return this.payment.paymentTypeDescription || '-'
            }
            if (this.payment.allocations.length === 1) {
                return this.payment.allocations[0].paymentTypeDescription
            }
            return 'Multiple'
        },
        formattedAmount() {
            if (!this.payment.paidValue) return '-'
            return this.formatPrice(this.payment.paidValue) + this.payment.currency
        },
        hasPaymentItems() {
            return this.payment.components && this.payment.components.length > 0
        },
        groupedPayments() {
            if (!this.payment.isGrouped || !this.payment.transactionCode) return null
            return this.payments.filter(p => p.transactionCode === this.payment.transactionCode)
        },
        // Grouped transactions render each sub-payment; a single payment renders as a one-item list.
        displayPayments() {
            return this.groupedPayments || [this.payment]
        },
    },
    methods: {
        ...mapActions([
            'deletePayment',
            'setSelectedInvoice',
            'refreshCurrentSelectedReservation'
        ]),
        buildSummaryForPayment(p) {
            const bookingGenderPrices = (this.getSelectedReservation && this.getSelectedReservation.bookingGenderPrices) || []
            const bookingComponents = (this.getSelectedReservation && this.getSelectedReservation.bookingComponents) || []

            let items = []
            let itemsLabel = null

            if (!p.isFullAmount) {
                const ticketItems = (p.tickets || []).map(item => {
                    const match = bookingGenderPrices.find(gp => gp.id === item.itemId)
                    return {
                        label: (match ? match.audienceTypeDescription : '') + ' x' + item.originalQuantity,
                        total: item.originalQuantity * item.grossPrice
                    }
                })
                const componentItems = (p.components || []).map(item => {
                    const match = bookingComponents.find(bc => bc.id === item.itemId)
                    return {
                        label: match ? `${match.component.name} - ${match.componentPriceName} x${item.quantity}` : '',
                        total: item.quantity * item.grossPrice
                    }
                })
                items = [...ticketItems, ...componentItems]

                const parts = []
                    ; (p.tickets || []).forEach(item => {
                        const match = bookingGenderPrices.find(gp => gp.id === item.itemId)
                        if (match) parts.push(`${match.audienceTypeDescription} x ${item.originalQuantity}`)
                    })
                    ; (p.components || []).forEach(item => {
                        const match = bookingComponents.find(bc => bc.id === item.itemId)
                        if (match) parts.push(`${match.component.name} - ${match.componentPriceName} x ${item.quantity}`)
                    })
                itemsLabel = parts.join(', ') || '—'
            } else {
                const components = p.components || []
                items = components.reduce((allItems, component) => {
                    const lines = (component.lines || []).map(line => ({
                        label: line.description,
                        total: line.amount
                    }))
                    return allItems.concat(lines)
                }, [])
                const firstComponent = components[0]
                itemsLabel = firstComponent ? firstComponent.label : null
            }

            return {
                reference: p.reference,
                itemsLabel,
                items,
                paymentMethods: (p.allocations || []).map(allocation => ({
                    typeName: allocation.paymentTypeDescription,
                    amountReceived: allocation.amount
                })),
                change: (p.allocations || []).reduce((sum, a) => sum + (a.change || 0), 0),
                createInvoice: p.invoices && p.invoices.length > 0,
                canBeInvoiced: p.canBeInvoiced,
                hasBillingDetails: p.hasBillingDetails,
                invoicedOnCreate: p.invoicedOnCreate,
                billingName: p.billing && p.billing.details ? p.billing.details.businessName || '' : '',
            }
        },
        showDeleteModal() {
            this.isConfirmingDelete = true
        },
        async confirmDelete() {
            if (this.isDeleting) return
            this.isDeleting = true
            const result = await this.deletePayment(this.payment.id)
            this.isDeleting = false
            if (result.ok) this.$emit('deleted')
        },
        invoiceTypeLabel(invoice) {
            const match = INVOICE_TYPES.find(t => t.id === invoice.invoiceType)
            return match ? match.label : null
        },
        openInvoiceDetailsModal(invoice) {
            this.setSelectedInvoice(invoice);
            this.showInvoiceDetailsModal = true;
        },
        handleInvoiceModalToggle(action) {
            if (action === 'close') {
                this.showInvoiceDetailsModal = false;
                this.setSelectedInvoice(null);
            }
        },
        openNonLegacyInvoicesModal(invoice) {
            this.selectedInvoiceId = invoice.id;
            this.bookingId = invoice.bookingId;
            this.isNonLegacyInvoiceModalOpen = true;
        },
        async onSavePayInEdit(destinationPaymentId) {
            if (this.isSaving) return
            this.isSaving = true
            const result = await invoiceService.moveInvoice(this.bookingId, this.selectedInvoiceId, destinationPaymentId)
            if (!result.ok) {
                this.isSaving = false
                this.$toasted.error(result.message)
                return
            }
            await this.refreshCurrentSelectedReservation()
            this.isSaving = false
            this.isNonLegacyInvoiceModalOpen = false
            this.$emit('saved')
        },
        toggleCreateInvoiceModal() {
            this.showCreateInvoiceModal = !this.showCreateInvoiceModal;
            if (!this.showCreateInvoiceModal) {
                this.invoicePaymentId = null;
            }
        },
        canCreateInvoiceForPayment(p) {
            if (!this.isToShowInvoicing || !this.getShowInvoiceOption) return false
            if (this.getIsSavingReservation || this.invoiceCreating) return false
            return p.canBeInvoiced;
        },
        onInvoiceCreated() {
            this.invoiceCreating = true
        },
        openCreateInvoiceForPayment(paymentId) {
            if (this.reservationHasUnsavedChanges) {
                this.$dialog.alert(
                    `<div class="d-flex flex-column justify-content-center">
                        <div class="fs-largest font-weight-600 mb-4">There are unsaved changes!</div>
                        <div class="bold dark">You must save the reservation before invoicing.</div>
                    </div>`,
                    { okText: 'Yes', cancelText: 'No' })
                return false
            }
            this.invoicePaymentId = paymentId
            this.showCreateInvoiceModal = true
        },
    },
    components: {
        BillingDetailsForm,
        PayInSummaryCard,
        NonLegacyInvoicesModal,
        CreateInvoiceModal,
        InvoiceModal,
        Loader,
        LoadingButton,
    }
}
</script>

<style lang="scss">
body.payin-details-open {
    overflow: hidden;

    .modal {
        overflow: hidden;
    }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/scss/_variables.scss';

.pay-in-details-modal {
    &__overlay {
        background-color: rgba(0, 0, 0, 0.5);
        width: 100%;
        height: 100%;
        z-index: 20;
        position: fixed;
        top: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__container {
        width: 52rem;
        height: 42rem;
        max-height: 75vh;
        max-width: 100%;
        border-radius: 0.25rem;
        position: relative;
        overflow: hidden;
    }

    &__header {
        flex: 0 0 auto;
    }

    &__footer {
        flex: 0 0 auto;
        border-top: 0.0625rem solid $light;
    }

    &__body {
        flex: 1 1 auto;
        overflow-y: auto;
        min-height: 0;

        scrollbar-gutter: stable;

        /* Visible scrollbar so users can tell there is more content */
        scrollbar-width: thin;
        /* Firefox */
        scrollbar-color: $medium transparent;
        /* Firefox */

        &::-webkit-scrollbar {
            /* Chrome / Safari */
            width: 0.5rem;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: $medium;
            border-radius: 0.25rem;
        }
    }

    &__confirm-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.35);
        border-radius: 0.25rem;
        z-index: 1;
    }

    &__confirm-box {
        width: 28rem;
        border-radius: 0.25rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    &__padded-row {
        padding-right: 0;
    }

    &__divider {
        border-top: 0.0625rem solid $medium;
        margin-bottom: 1rem;
    }

    &__invoice-table {
        border: 0.0625rem solid $light;
        border-radius: 0.25rem;
        overflow: hidden;

        table {
            border-collapse: collapse;
            width: 100%;
        }

        th {
            font-size: $font-size-small !important;
            color: $dark;
            padding: 0.2rem 1rem;
            background-color: $lighter;
            border-bottom: 0.0625rem solid $light;
            text-align: left;
        }

        td {
            font-size: $font-size-small;
            padding: 0.6rem 1rem;
            color: $darker;
            border-bottom: 0.0625rem solid $light;
        }

        tr:last-child td {
            border-bottom: none;
        }
    }

    &__invoice-loader {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(255, 255, 255, 0.7);
        z-index: 2;
    }

    &__invoice-row {
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: rgba(0, 0, 0, 0.02);
        }
    }

    &__invoice-btn {
        font-size: 0.65rem;
        padding: 0.25rem 0.4rem;
        line-height: 1;
        min-height: unset;
        height: auto;
    }

    &__invoice-btn--disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    &__btn-height {
        height: 39px;
    }

    &__btn--disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
}
</style>


// WEBPACK FOOTER //
// PayInDetailsModal.vue?6c2c9aab